import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { ColumnInterface, SortFn } from "./interfaces";
import {
  EmptyNotice,
  StyledAddRow,
  StyledContainer,
  StyledTable,
  StyledTableBody,
  StyledToolbar,
} from "./table.styles";
import TableRow from "./row";
import TableCell from "./cells/cell";
import TableSearch from "./search";
import Button from "components/button";
import TableDeleteCell from "./cells/delete-cell";
import TableEditCell from "./cells/edit-cell";
import TableMoveCell from "./cells/move-cell";
import SortIndicator from "./sort-indicator";
import { faPlus, IconType } from "components/icon-library";
import TableStatusCell from "./cells/status-cell";
import { useTranslate } from "stores/hooks/translation";

interface AddedInterface {
  isDisabled?: () => boolean;
  text: string;
  icon?: IconType;
  onClick: () => Promise<void>;
}

interface DeleterInterface {
  isDisabled?: (item: any) => boolean;
  onClick: (item: any) => Promise<void>;
}

interface EditerInterface {
  isDisabled?: (item: any) => boolean;
  onClick: (item: any) => Promise<void>;
  icon?: IconType;
}

interface StatusInterface {
  check: (item: any) => boolean;
  icon?: IconType;
}

interface MoverInterface {
  isUpDisabled?: (item: any) => boolean;
  isDownDisabled?: (item: any) => boolean;
  onUp: (item: any) => Promise<void>;
  onDown: (item: any) => Promise<void>;
}

interface TableProps {
  // will be used to persist sorting etc
  name: string;

  // title, rendered if non-undefined
  title?: string;

  // schema for sorting
  columns: ColumnInterface[];

  // data for the table
  data: any[];
  noItemsText?: string;

  // must have sorted for the data
  sortFn?: SortFn;

  // can be searched?
  search?: boolean;
  searchHint?: string;

  // show index table
  index?: boolean;

  // actions
  adder?: AddedInterface;
  mover?: MoverInterface;
  editers?: EditerInterface[];
  statuses?: StatusInterface[];
  deleter?: DeleterInterface;
}

const Table: FunctionComponent<TableProps> = ({
  name,
  columns,
  data,
  noItemsText,
  sortFn,
  search = false,
  searchHint,
  index = false,
  adder,
  mover,
  editers = [],
  statuses = [],
  deleter,
}) => {
  const translate = useTranslate();

  const [keyword, toggleKeyword] = useState("");
  const [selected, toggleSelected] = useState<any[]>([]);

  // data stages
  const [searchedData, toggleSearchedData] = useState<any[]>([]);
  const [sortedData, toggleSortedData] = useState<any[]>([]);

  // first filter data
  useEffect(() => {
    const matched =
      search && keyword
        ? data.filter((item) => {
            // match the row for each column
            for (let i = 0; i < columns.length; i += 1) {
              const column = columns[i];
              // doMatch defined and
              if (column.search && column.search(item, keyword, column)) {
                return true;
              }
            }

            // no match
            return false;
          })
        : data;

    toggleSearchedData(matched);
  }, [data, keyword]);

  // remove selection from items that are filtered out
  useEffect(() => {
    const arr = selected.filter((s) => searchedData.includes(s));
    toggleSelected(arr);
  }, [searchedData]);

  // sort data
  useEffect(() => {
    toggleSortedData(sortFn ? searchedData.sort(sortFn) : searchedData);
  }, [searchedData]);

  const renderAdder = (adder: AddedInterface) => {
    const { icon, onClick, isDisabled = () => false } = adder;
    return (
      <StyledAddRow>
        <Button
          type="tertiary"
          postIcon={icon || faPlus}
          onClick={onClick}
          disabled={isDisabled()}
        >
          {adder.text}
        </Button>
      </StyledAddRow>
    );
  };

  const renderDeleter = (deleter: DeleterInterface, item: any) => {
    const { isDisabled = () => false, onClick } = deleter;
    return (
      <TableDeleteCell
        disabled={isDisabled(item)}
        onDelete={onClick}
        item={item}
      />
    );
  };

  const renderMover = (mover: MoverInterface, item: any) => {
    const {
      isUpDisabled = () => false,
      isDownDisabled = () => false,
      onUp,
      onDown,
    } = mover;
    const upDisabled = isUpDisabled(item);
    const downDisabled = isDownDisabled(item);
    return (
      <TableMoveCell
        key={`mover`}
        item={item}
        isUpDisabled={upDisabled}
        isDownDisabled={downDisabled}
        onUp={onUp}
        onDown={onDown}
      />
    );
  };

  const renderEditer = (editer: EditerInterface, order: number, item: any) => {
    const { onClick, isDisabled = () => false, icon } = editer;
    const key = `editer-${order}`;
    return (
      <TableEditCell
        key={key}
        disabled={isDisabled(item)}
        onClick={onClick}
        item={item}
        icon={icon}
      />
    );
  };

  const renderStatus = (status: StatusInterface, order: number, item: any) => {
    const { check = () => false, icon } = status;
    const key = `status-${order}`;
    if (check(item) === false) {
      return <TableCell key={key} />;
    }
    return <TableStatusCell key={key} icon={icon} />;
  };

  return (
    <StyledContainer>
      {search && (
        <StyledToolbar>
          {search && (
            <TableSearch
              keyword={keyword}
              hint={searchHint}
              onChange={(keyword: string) => toggleKeyword(keyword)}
            />
          )}
        </StyledToolbar>
      )}
      <StyledTable id={name}>
        <TableRow header>
          {index && (
            <TableCell header key="index" width={50}>
              # <SortIndicator direction="asc" />
            </TableCell>
          )}
          {columns.map((column) => {
            return (
              <TableCell header key={column.key} width={column.width}>
                {column.header}{" "}
                {column.indicator && (
                  <SortIndicator direction={column.indicator} />
                )}
              </TableCell>
            );
          })}
          {statuses.map((_e, index) => (
            <TableCell key={`status-${index}`} header width={44} />
          ))}
          {editers.map((_e, index) => (
            <TableCell key={`editer-${index}`} header width={44} />
          ))}
          {mover && (
            <Fragment>
              <TableCell key="down" header width={34} />
              <TableCell key="up" header width={34} />
            </Fragment>
          )}
          {deleter && <TableCell key="deleter" header width={44} />}
        </TableRow>
        <StyledTableBody>
          {sortedData.map((item, rowIndex) => {
            return (
              <TableRow key={`${rowIndex}`} selected={selected.includes(item)}>
                {index && (
                  <TableCell key="order" width={54}>
                    #{item.order + 1}
                  </TableCell>
                )}
                {columns.map((column, columnIndex) => {
                  return (
                    <TableCell key={`${rowIndex}-${columnIndex}`}>
                      {column.render(item, column)}
                    </TableCell>
                  );
                })}
                {statuses.map((status, index) =>
                  renderStatus(status, index, item),
                )}
                {editers.map((editer, index) =>
                  renderEditer(editer, index, item),
                )}
                {mover && renderMover(mover, item)}
                {deleter && renderDeleter(deleter, item)}
              </TableRow>
            );
          })}
          {sortedData.length === 0 && (
            <TableRow>
              {index && <TableCell></TableCell>}
              {columns.map((_column, columnIndex) => {
                return (
                  <TableCell key={`empty-${columnIndex}`}>&nbsp;</TableCell>
                );
              })}
              {statuses.map((_e, index) => (
                <TableCell key={index}>&nbsp;</TableCell>
              ))}
              {editers.map((_e, index) => (
                <TableCell key={index}>&nbsp;</TableCell>
              ))}
              {mover && (
                <Fragment>
                  <TableCell>&nbsp;</TableCell>
                  <TableCell>&nbsp;</TableCell>
                </Fragment>
              )}
              {deleter && <TableCell>&nbsp;</TableCell>}
            </TableRow>
          )}
        </StyledTableBody>
      </StyledTable>
      {sortedData.length === 0 && (
        <EmptyNotice>{noItemsText || translate("no_rows")}</EmptyNotice>
      )}
      {adder && renderAdder(adder)}
    </StyledContainer>
  );
};

export default Table;
